import { getActiveTagNames } from '@playbooks/action-cards-utils'
import { ActionCard, ElementWithCodeAndValue, WidgetType } from '@playbooks/core/src/interfaces/ActionCard'

import { getNode, traverseNodes } from '../actionCardOperations'
import processConditionalReplacements from '../processConditionalReplacements'
import { processReplacements } from '../processReplacements'
import { adjustActionCardForPrompt } from './adjustActionCardForPrompt'
import { WidgetContext } from '../../features/widgets/widgetTypes'

/** Options for the processPromptReplacements function */
interface ProcessPromptReplacementsOptions {
  /** The prompt to be adjusted */
  prompt: string
  /** The Action Card in which context the prompt is generated */
  actionCardsData: ActionCard
  /** Questions whose answers can be referenced with `$questionCode` */
  elementsWithCode: ElementWithCodeAndValue[]
  /** The context of where the prompt is used */
  context?: WidgetContext
}

/** Placeholder replacement */
interface Replacement {
  /** The placeholder to be replaced */
  placeholder: string
  /** The actual value to replace the placeholder with */
  value: string
}

/**
 * Adjust the AI prompt before sending it to the AI model
 * by replacing special placeholders with the actual values.
 * @param prompt The prompt to be adjusted
 * @param actionCardsData The Action Card in which context the prompt is generated
 * @returns The adjusted prompt
 */
export default async function processPromptReplacements({
  prompt,
  actionCardsData,
  elementsWithCode,
  context,
}: ProcessPromptReplacementsOptions) {
  // Process `%if[ condition ] ... endif%` conditional blocks (#11433)
  // before injecting dynamic values that may contain conditional-like text
  const activeTags = getActiveTagNames(actionCardsData)
  prompt = processConditionalReplacements(prompt, activeTags || [])

  // Process `$questionCode` replacements
  prompt = processReplacements(prompt, elementsWithCode) || ''

  const preparedActionCard = adjustActionCardForPrompt(actionCardsData.sections)
  const section = adjustActionCardForPrompt(context?.section)
  const headerScopeSections = adjustActionCardForPrompt(context?.headerScopeSections)

  // Find the placeholders in the prompt to be replaced with the actual values
  const allReplacements: Replacement[] = [
    // Replacement for the Action Card JSON
    {
      placeholder: '[actionCard]',
      value: JSON.stringify(preparedActionCard),
    },
    // Replacement for the current Action Card node where the widget is rendered (#10482)
    {
      placeholder: '[context]',
      value: context?.originalText || '',
    },
    {
      placeholder: '[currentSection]',
      value: JSON.stringify(section || {}),
    },
    {
      placeholder: '[currentHeader]',
      value: JSON.stringify(headerScopeSections || []),
    },
    {
      placeholder: '[transcript]',
      value: getFirstTeammateTranscript(actionCardsData),
    },
    // Replacements for specific answers in the Action Card,
    // e.g. [answer#123] will be replaced with "value" prop of the question with ID 123 (#7852)
    ...[...prompt.matchAll(/\[answer#(\d+)\]/g)].map(([placeholder, id]) => ({
      placeholder,
      value: getNode(actionCardsData.sections, id)?.value,
    })),
  ]

  // Replace the placeholders in the prompt with the actual values
  allReplacements.forEach(r => {
    prompt = prompt.replaceAll(r.placeholder, r.value || '')
  })

  return prompt
}

/**
 * Find the first AiTeammate widget in the Action Card with a transcript and return its transcript.
 */
function getFirstTeammateTranscript(actionCardsData: ActionCard): string {
  let transcript = ''

  traverseNodes(actionCardsData.sections, '*', (obj, key) => {
    if (transcript) return

    const value = obj[key]
    if (
      value?.type === WidgetType.AiTeammate &&
      typeof value.value === 'object' &&
      typeof value.value?.transcript === 'string' &&
      value.value.transcript.trim()
    ) {
      transcript = value.value.transcript
    }
  })

  return transcript
}
